// Tilt-to-explore — mobile magic-window pan via deviceorientation.
//
// On phones (coarse pointer + DeviceOrientationEvent supported), tilting
// the device pans the panorama. Like Insta360's app or YouTube 360 videos
// on mobile — the phone is the viewport, and pointing it around the room
// rotates the camera correspondingly.
//
// UX flow:
//   1. Operator/buyer taps the 🔭 button (revealed only on mobile).
//   2. iOS 13+ requires an explicit permission grant for motion events;
//      this triggers `DeviceOrientationEvent.requestPermission()`. On
//      Android / older iOS the permission is granted by default.
//   3. The first tilt sample is captured as the "neutral" orientation.
//   4. Subsequent samples delta against neutral → yaw + pitch adjustments
//      blended into CameraControls.setLook (5 Hz update rate, smoothed by
//      the existing camera-controls lerp).
//   5. Tap the button again to disable.
//
// Coexists with drag-look: dragging still works while tilt is on, and
// counts as user input. The neutral orientation re-samples on each enable
// so re-positioning the phone is non-disruptive.

import type { CameraControls } from './cameraControls';

const STORAGE_KEY = 'imersivo:tilt-enabled';

interface DeviceOrientationEventStatic {
  requestPermission?: () => Promise<'granted' | 'denied'>;
}

export class TiltControls {
  private readonly controls: CameraControls;
  private enabled = false;
  private neutral: { alpha: number; beta: number; gamma: number } | null = null;
  private button: HTMLButtonElement | null = null;
  private readonly onOrientation = (e: DeviceOrientationEvent): void => {
    if (!this.enabled) return;
    if (e.alpha === null || e.beta === null || e.gamma === null) return;
    if (!this.neutral) {
      this.neutral = { alpha: e.alpha, beta: e.beta, gamma: e.gamma };
      return;
    }
    // alpha = compass (yaw-like), beta = front-back tilt (pitch-like).
    // Delta from neutral so the phone's "rest" is the room's "center."
    const yawDelta = wrapDeg(e.alpha - this.neutral.alpha);
    const pitchDelta = e.beta - this.neutral.beta;
    const cur = this.controls.getLook();
    // Blend into the controls — set a target yaw/pitch that the smoothing
    // will ease toward. Scaling pitch down (0.6×) so over-tilt doesn't peg
    // against the existing PITCH_LIMIT_DEG cap.
    this.controls.setLook(cur.yaw + yawDelta * 0.04, cur.pitch + pitchDelta * 0.04);
    // Decay the neutral toward current so it self-recenters slowly — keeps
    // the operator from having to re-orient the phone every minute.
    this.neutral.alpha = lerpDeg(this.neutral.alpha, e.alpha, 0.005);
    this.neutral.beta = e.beta * 0.005 + this.neutral.beta * 0.995;
    this.neutral.gamma = e.gamma * 0.005 + this.neutral.gamma * 0.995;
  };

  constructor(controls: CameraControls) {
    this.controls = controls;
  }

  /** Wire the topbar button. Hides itself on devices without device-orientation
   *  support (most desktops + some embedded webviews). */
  install(): void {
    const btn = document.getElementById('tilt-button') as HTMLButtonElement | null;
    if (!btn) return;
    this.button = btn;
    if (!('DeviceOrientationEvent' in window) || !window.matchMedia('(pointer: coarse)').matches) {
      // Not a touch device — hide the button. Desktop users have drag-look.
      btn.style.display = 'none';
      return;
    }
    btn.addEventListener('click', () => void this.toggle());
    // Restore previous state — but skip auto-resume on first paint, since
    // iOS requires a fresh user gesture for the permission prompt.
    try {
      if (localStorage.getItem(STORAGE_KEY) === '1') this.renderButton(false);
    } catch {
      /* ignore */
    }
  }

  async toggle(): Promise<void> {
    if (this.enabled) {
      this.disable();
    } else {
      await this.enable();
    }
  }

  private async enable(): Promise<void> {
    const DOE = window.DeviceOrientationEvent as unknown as DeviceOrientationEventStatic;
    if (typeof DOE.requestPermission === 'function') {
      try {
        const grant = await DOE.requestPermission();
        if (grant !== 'granted') {
          console.warn('[tilt] orientation permission denied');
          return;
        }
      } catch (err) {
        console.warn('[tilt] permission request failed', err);
        return;
      }
    }
    this.neutral = null; // re-sample on next event
    this.enabled = true;
    window.addEventListener('deviceorientation', this.onOrientation);
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      /* ignore */
    }
    this.renderButton(true);
  }

  private disable(): void {
    this.enabled = false;
    window.removeEventListener('deviceorientation', this.onOrientation);
    this.neutral = null;
    try {
      localStorage.setItem(STORAGE_KEY, '0');
    } catch {
      /* ignore */
    }
    this.renderButton(false);
  }

  private renderButton(on: boolean): void {
    if (!this.button) return;
    const icon = this.button.querySelector('.icon-button__icon') as HTMLElement | null;
    if (icon) icon.textContent = on ? '🔭' : '🔭';
    this.button.setAttribute('aria-pressed', String(on));
    this.button.classList.toggle('is-active', on);
    this.button.setAttribute(
      'aria-label',
      on ? 'Desligar exploração por inclinação' : 'Ligar exploração por inclinação',
    );
  }
}

function wrapDeg(d: number): number {
  let x = d;
  while (x > 180) x -= 360;
  while (x < -180) x += 360;
  return x;
}

function lerpDeg(a: number, b: number, t: number): number {
  return a + wrapDeg(b - a) * t;
}
